import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Send, Image, X, Loader2, Phone, Calendar,
  CheckCheck, Check, Clock, MessageCircle, User, Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { chatWallpaperStyle } from '@/components/messages/chatWallpaper';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ==================== CONVERSATION LIST ====================
function ConsultationList({ user, onSelectChat, ws }) {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchConversations = useCallback(async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const res = await axios.get(`${API}/pandit-chat/conversations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConversations(res.data);
    } catch {
      toast.error('Failed to load chats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Listen for new messages to refresh list
  useEffect(() => {
    if (!ws) return;
    const handler = (event) => {
      if (event.data === 'pong') return;
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'pandit_chat_message') {
          fetchConversations();
        }
      } catch {}
    };
    ws.addEventListener('message', handler);
    return () => ws.removeEventListener('message', handler);
  }, [ws, fetchConversations]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="text-center py-16 px-4">
        <MessageCircle className="w-14 h-14 mx-auto text-slate-200 mb-4" />
        <h3 className="font-semibold text-slate-600 mb-1">No Consultations Yet</h3>
        <p className="text-sm text-slate-400 mb-4">
          Chat becomes available after a booking is confirmed or paid
        </p>
        <Button
          onClick={() => navigate('/pandit')}
          className="rounded-full bg-[#FF9933] hover:bg-[#E68A00]"
          data-testid="find-pandit-btn"
        >
          Find Pandit Ji
        </Button>
      </div>
    );
  }

  return (
    <div className="divide-y divide-slate-100" data-testid="consultation-list">
      {conversations.map((chat) => (
        <button
          key={chat.chat_id}
          onClick={() => onSelectChat(chat)}
          className="w-full px-4 py-3 flex items-center gap-3 hover:bg-orange-50/50 transition-colors text-left"
          data-testid={`chat-item-${chat.chat_id}`}
        >
          <div className="relative flex-shrink-0">
            <img
              src={chat.other_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(chat.other_name)}&background=FF9933&color=fff&size=48`}
              alt=""
              className="w-12 h-12 rounded-full object-cover"
            />
            {chat.other_role === 'pandit' && (
              <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-orange-500 rounded-full flex items-center justify-center">
                <Shield className="w-2.5 h-2.5 text-white" />
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-0.5">
              <span className="font-semibold text-sm text-slate-800 truncate">{chat.other_name}</span>
              {chat.last_message_at && (
                <span className="text-[10px] text-slate-400 flex-shrink-0 ml-2">
                  {formatTime(chat.last_message_at)}
                </span>
              )}
            </div>
            <p className="text-xs text-orange-500 mb-0.5">{chat.pooja_type}</p>
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-400 truncate">
                {chat.last_message || 'Start the conversation...'}
              </p>
              {chat.unread_count > 0 && (
                <span className="ml-2 flex-shrink-0 w-5 h-5 rounded-full bg-orange-500 text-white text-[10px] flex items-center justify-center font-bold">
                  {chat.unread_count > 9 ? '9+' : chat.unread_count}
                </span>
              )}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

// ==================== CHAT WINDOW ====================
function ChatWindow({ chatId, user, ws, onBack }) {
  const [messages, setMessages] = useState([]);
  const [chatInfo, setChatInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [typing, setTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const token = localStorage.getItem('dharmbook_token');
  const headers = { Authorization: `Bearer ${token}` };

  // Fetch messages
  useEffect(() => {
    if (!chatId) return;
    fetchMessages();
  }, [chatId]);

  // Auto scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // WebSocket listener for incoming messages
  useEffect(() => {
    if (!ws || !chatId) return;
    const handler = (event) => {
      if (event.data === 'pong') return;
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'pandit_chat_message' && data.data.chat_id === chatId) {
          setMessages(prev => [...prev, data.data.message]);
          // Mark as read
          markAsRead();
        }
        if (data.type === 'pandit_chat_typing' && data.data.chat_id === chatId) {
          setTyping(true);
          clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = setTimeout(() => setTyping(false), 2000);
        }
      } catch {}
    };
    ws.addEventListener('message', handler);
    return () => ws.removeEventListener('message', handler);
  }, [ws, chatId]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/pandit-chat/messages/${chatId}`, { headers });
      setMessages(res.data.messages || []);
      setChatInfo(res.data.chat);
    } catch (err) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async () => {
    try {
      await axios.get(`${API}/pandit-chat/messages/${chatId}?limit=1`, { headers });
    } catch {}
  };

  const handleSend = async () => {
    if ((!text.trim() && !imageFile) || sending) return;

    let mediaUrl = null;
    let mediaType = null;

    // Upload image
    if (imageFile) {
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append('file', imageFile);
        const uploadRes = await axios.post(`${API}/pandit-chat/upload-image`, formData, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        });
        mediaUrl = uploadRes.data.media_url;
        mediaType = uploadRes.data.media_type;
      } catch {
        toast.error('Failed to upload image');
        setUploading(false);
        return;
      }
      setUploading(false);
    }

    setSending(true);
    try {
      const res = await axios.post(`${API}/pandit-chat/send`, {
        chat_id: chatId,
        text: text.trim(),
        media_url: mediaUrl,
        media_type: mediaType,
      }, { headers });

      // Add optimistically
      setMessages(prev => [
        ...prev,
        {
          id: res.data.message_id,
          chat_id: chatId,
          sender_id: user.id,
          sender_name: user.name,
          sender_avatar: user.avatar,
          text: text.trim(),
          media_url: mediaUrl,
          media_type: mediaType,
          status: 'sent',
          created_at: res.data.created_at,
        },
      ]);
      setText('');
      clearImage();
    } catch {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleTyping = () => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'pandit_chat_typing', chat_id: chatId }));
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Only images are allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image too large (max 5MB)');
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const clearImage = () => {
    setImageFile(null);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const otherName = chatInfo
    ? (chatInfo.devotee_name === user?.name ? chatInfo.pandit_name : chatInfo.devotee_name)
    : '';
  const otherAvatar = chatInfo
    ? (chatInfo.devotee_name === user?.name ? chatInfo.pandit_avatar : chatInfo.devotee_avatar)
    : '';

  return (
    <div className="flex flex-col h-full" data-testid="chat-window">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3 flex-shrink-0">
        <button onClick={onBack} className="text-slate-500 hover:text-slate-700 md:hidden" data-testid="chat-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <img
          src={otherAvatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(otherName)}&background=FF9933&color=fff&size=40`}
          alt=""
          className="w-10 h-10 rounded-full object-cover"
        />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-slate-800 truncate">{otherName}</p>
          {typing ? (
            <p className="text-xs text-green-500 animate-pulse">typing...</p>
          ) : chatInfo?.pooja_type ? (
            <p className="text-xs text-orange-500">{chatInfo.pooja_type} &middot; {chatInfo.booking_date}</p>
          ) : null}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2" data-testid="messages-area" style={chatWallpaperStyle(false)}>
        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-6 h-6 text-orange-400 animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-10">
            <MessageCircle className="w-10 h-10 mx-auto text-slate-200 mb-2" />
            <p className="text-sm text-slate-400">Start the conversation</p>
            <p className="text-xs text-slate-300 mt-1">Discuss pooja details, requirements, or ask questions</p>
          </div>
        ) : (
          <>
            {/* Booking Context Banner */}
            {chatInfo && (
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 text-center mb-3">
                <p className="text-xs text-orange-600">
                  <Calendar className="w-3 h-3 inline mr-1" />
                  {chatInfo.pooja_type} &middot; {chatInfo.booking_date}
                </p>
              </div>
            )}
            {messages.map((msg) => {
              const isOwn = msg.sender_id === user?.id;
              return (
                <div
                  key={msg.id}
                  className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                  data-testid={`msg-${msg.id}`}
                >
                  <div
                    className={`max-w-[75%] rounded-2xl px-3.5 py-2 ${
                      isOwn
                        ? 'bg-[#FF9933] text-white rounded-br-md'
                        : 'bg-white border border-slate-200 text-slate-800 rounded-bl-md'
                    }`}
                  >
                    {!isOwn && (
                      <p className={`text-[10px] font-medium mb-0.5 ${isOwn ? 'text-white/70' : 'text-orange-500'}`}>
                        {msg.sender_name}
                      </p>
                    )}
                    {msg.media_url && msg.media_type === 'image' && (
                      <img
                        src={msg.media_url.startsWith('http') ? msg.media_url : `${BACKEND_URL}${msg.media_url}`}
                        alt=""
                        className="rounded-lg mb-1 max-w-full max-h-48 object-cover cursor-pointer"
                        onClick={() => window.open(msg.media_url.startsWith('http') ? msg.media_url : `${BACKEND_URL}${msg.media_url}`, '_blank')}
                      />
                    )}
                    {msg.text && <p className="text-sm whitespace-pre-wrap break-words">{msg.text}</p>}
                    <div className={`flex items-center gap-1 mt-0.5 ${isOwn ? 'justify-end' : 'justify-start'}`}>
                      <span className={`text-[10px] ${isOwn ? 'text-white/60' : 'text-slate-400'}`}>
                        {formatMessageTime(msg.created_at)}
                      </span>
                      {isOwn && (
                        msg.status === 'seen' ? <CheckCheck className="w-3 h-3 text-white/80" /> :
                        msg.status === 'delivered' ? <CheckCheck className="w-3 h-3 text-white/50" /> :
                        <Check className="w-3 h-3 text-white/50" />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Image Preview */}
      {imagePreview && (
        <div className="px-4 py-2 bg-white border-t border-slate-200">
          <div className="relative inline-block">
            <img src={imagePreview} alt="" className="h-20 rounded-lg object-cover" />
            <button
              onClick={clearImage}
              className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}

      {/* Input */}
      <div className="bg-white border-t border-slate-200 px-3 py-2 flex items-center gap-2 flex-shrink-0" data-testid="chat-input-area">
        <input
          type="file"
          ref={fileInputRef}
          accept="image/*"
          className="hidden"
          onChange={handleImageSelect}
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          className="p-2 text-slate-400 hover:text-orange-500 transition-colors rounded-full hover:bg-orange-50"
          data-testid="attach-image-btn"
        >
          <Image className="w-5 h-5" />
        </button>
        <input
          type="text"
          value={text}
          onChange={(e) => { setText(e.target.value); handleTyping(); }}
          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
          placeholder="Type a message..."
          className="flex-1 px-4 py-2 bg-slate-100 rounded-full text-sm outline-none focus:ring-2 focus:ring-orange-200 transition-all"
          data-testid="chat-text-input"
        />
        <button
          onClick={handleSend}
          disabled={(!text.trim() && !imageFile) || sending || uploading}
          className="p-2 bg-[#FF9933] text-white rounded-full hover:bg-[#E68A00] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="send-message-btn"
        >
          {sending || uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
        </button>
      </div>
    </div>
  );
}

// ==================== MAIN PAGE ====================
export default function PanditChatPage({ user }) {
  const { chatId: urlChatId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeChatId, setActiveChatId] = useState(urlChatId || null);
  const [ws, setWs] = useState(null);

  // Auto-start chat from booking
  useEffect(() => {
    const bookingId = searchParams.get('booking');
    if (bookingId) {
      startChatFromBooking(bookingId);
    }
  }, [searchParams]);

  // WebSocket setup
  useEffect(() => {
    const token = localStorage.getItem('dharmbook_token');
    if (!token) return;

    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const socket = new WebSocket(`${wsUrl}/ws/${token}`);

    socket.onopen = () => {
      const pingInterval = setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) socket.send('ping');
      }, 30000);
      socket._pingInterval = pingInterval;
    };

    socket.onclose = () => {
      if (socket._pingInterval) clearInterval(socket._pingInterval);
    };

    setWs(socket);
    return () => {
      if (socket._pingInterval) clearInterval(socket._pingInterval);
      socket.close();
    };
  }, []);

  const startChatFromBooking = async (bookingId) => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const res = await axios.post(
        `${API}/pandit-chat/start?booking_id=${bookingId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setActiveChatId(res.data.chat_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cannot start chat');
    }
  };

  if (!user) {
    navigate('/auth');
    return null;
  }

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="pandit-chat-page">
      {/* Mobile Header */}
      <div className="bg-gradient-to-r from-[#FF9933] to-[#FF6600] text-white px-4 py-3 flex items-center gap-3 flex-shrink-0">
        <button
          onClick={() => activeChatId ? setActiveChatId(null) : navigate(-1)}
          className="text-white/80 hover:text-white"
          data-testid="chat-page-back"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="font-bold text-base" style={{ fontFamily: 'Merriweather, serif' }}>
            {activeChatId ? '' : 'Pandit Consultations'}
          </h1>
          {!activeChatId && (
            <p className="text-xs text-orange-100">Chat with your booked Pandit Ji</p>
          )}
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden" style={{ height: 'calc(100vh - 120px)' }}>
        {/* Sidebar / List */}
        <div className={`w-full md:w-80 md:border-r border-slate-200 overflow-y-auto flex-shrink-0 ${activeChatId ? 'hidden md:block' : ''}`}>
          <ConsultationList
            user={user}
            ws={ws}
            onSelectChat={(chat) => setActiveChatId(chat.chat_id)}
          />
        </div>

        {/* Chat Window */}
        <div className={`flex-1 flex flex-col ${!activeChatId ? 'hidden md:flex' : ''}`}>
          {activeChatId ? (
            <ChatWindow
              chatId={activeChatId}
              user={user}
              ws={ws}
              onBack={() => setActiveChatId(null)}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center bg-slate-50">
              <div className="text-center">
                <MessageCircle className="w-16 h-16 mx-auto text-slate-200 mb-4" />
                <p className="text-slate-400 text-sm">Select a conversation to start chatting</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ==================== HELPERS ====================
function formatTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now - d) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return d.toLocaleDateString('en-IN', { weekday: 'short' });
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

function formatMessageTime(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
}
